/**
 * Action executor — handles server commands that control the page.
 * Supports navigate, scroll_to, click, and a localStorage-based
 * action stack for post-navigation follow-up actions.
 */

import type { StackAction, LeadForm, LeadFormField } from "./messages";
import type { ProductCard } from "@bluehat/shared/events/schema";
import { debugLog } from "./debug.ts";

const STACK_KEY = "bh_action_stack";

type Command = {
  action: string;
  text?: string;
  url?: string;
  selector?: string;
  scrollTo?: string;
  delay?: number;
  then?: StackAction[];
  autoNavigate?: boolean;
  label?: string;
  ctaSlug?: string;
  sendText?: string;
  chips?: Array<{ label: string; sendText: string }>;
  /** `products` command payload. Typed from the SERVER's ProductCardSchema, not
   *  re-declared — the surrounding fields in this type are a hand-written mirror
   *  of CommandMessage, which is itself a mirror of the shared command union, and
   *  three copies of a shape is three chances for it to drift. */
  products?: ProductCard[];
  turnId?: string;
  kind?: "answer" | "context_next_ask";
  deferred?: boolean;
  openInNewTab?: boolean;
  title?: string;
  fields?: LeadFormField[];
  submitLabel?: string;
};

export type CommandHandler = {
  openChat: () => void;
  closeChat: () => void;
  /** Present a navigate CTA as a clickable button in the chat (the
   *  default, vs. an immediate redirect). */
  presentCta: (url: string, label?: string, slug?: string, openInNewTab?: boolean) => void;
  /** Present a lead-capture form inline in the chat (mode="form"). */
  presentForm: (form: LeadForm) => void;
  /** Present a rich-lead offer chip — a one-tap accept button. */
  presentChip: (label: string, sendText: string) => void;
  /** Present 2–4 answer-choice chips (answers to the AI's last question)
   *  or context_next_ask visitor questions after an answer-only dead end.
   *  `deferred` = chips minted after their answer already shipped (decoupled
   *  ladder): attach quietly, never force the panel open. */
  /** Present product cards attached to this answer. The figures on them are the
   *  record's and the answer's prose states none, so dropping these silently
   *  would leave a reply that describes products without ever pricing them. */
  presentProducts: (products: ProductCard[]) => void;
  presentAnswerChips: (
    chips: Array<{ label: string; sendText: string }>,
    turnId: string,
    kind?: "answer" | "context_next_ask",
    deferred?: boolean,
  ) => void;
};

export function executeCommand(cmd: Command, handlers: CommandHandler) {
  switch (cmd.action) {
    case "open_chat":
      handlers.openChat();
      break;
    case "close_chat":
      handlers.closeChat();
      break;
    case "show_message":
      if (cmd.text) debugLog("[BlueHat] server message:", cmd.text);
      break;
    case "navigate":
      // PRESENT the URL by default — the visitor chooses to go. Only
      // auto-redirect when the CTA explicitly opts in (autoNavigate).
      if (cmd.autoNavigate) {
        navigateTo(cmd.url!, cmd.scrollTo, cmd.then);
      } else {
        handlers.presentCta(cmd.url!, cmd.label, cmd.ctaSlug, cmd.openInNewTab);
      }
      break;
    case "scroll_to":
      scrollTo(cmd.selector!);
      break;
    case "click":
      clickElement(cmd.selector!, cmd.delay);
      break;
    case "lead_form":
      handlers.presentForm({ title: cmd.title, fields: cmd.fields ?? [], submitLabel: cmd.submitLabel });
      break;
    case "quick_reply":
      if (cmd.label && cmd.sendText) handlers.presentChip(cmd.label, cmd.sendText);
      break;
    case "answer_chips":
      if (cmd.chips && cmd.chips.length >= 2 && cmd.turnId) {
        handlers.presentAnswerChips(cmd.chips, cmd.turnId, cmd.kind, cmd.deferred);
      }
      break;
    case "products":
      if (cmd.products && cmd.products.length > 0) handlers.presentProducts(cmd.products);
      break;
  }
}

function navigateTo(url: string, scrollTo?: string, then?: StackAction[]) {
  const stack: StackAction[] = [];

  // If we need to scroll after arriving, prepend it
  if (scrollTo) {
    stack.push({ action: "scroll_to", selector: scrollTo });
  }

  // Append any follow-up actions
  if (then) {
    stack.push(...then);
  }

  // Save action stack before navigating
  if (stack.length > 0) {
    try {
      localStorage.setItem(STACK_KEY, JSON.stringify(stack));
    } catch { /* storage full or unavailable */ }
  }

  window.location.href = url;
}

function scrollTo(selector: string) {
  const el = document.querySelector(selector);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

function clickElement(selector: string, delay?: number) {
  setTimeout(() => {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (el) el.click();
  }, delay ?? 500);
}

/**
 * Drain the action stack after page load.
 * Called once on snippet initialization — executes pending
 * post-navigation actions (scroll, click) after the page stabilizes.
 */
export function drainActionStack() {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STACK_KEY);
    localStorage.removeItem(STACK_KEY);
  } catch {
    return;
  }

  if (!raw) return;

  let stack: StackAction[];
  try {
    stack = JSON.parse(raw);
    if (!Array.isArray(stack)) return;
  } catch {
    return;
  }

  // Wait for page stability before executing actions
  const run = () => {
    let delay = 0;
    for (const action of stack) {
      switch (action.action) {
        case "scroll_to":
          setTimeout(() => scrollTo(action.selector), delay);
          delay += 300;
          break;
        case "click":
          delay += action.delay ?? 500;
          setTimeout(() => clickElement(action.selector, 0), delay);
          delay += 100;
          break;
      }
    }
  };

  if ("requestIdleCallback" in window) {
    requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 1000);
  }
}
